import {Component} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {IBaseCRUDComponent} from '../../../../sdk/src/components/ibase-crud/ibase-crud.component';
import {CpmCCustomerUserMembershipApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-common/cpmc-customer-user-membership.api-model';
import {
    NewEditCustomerUserMembershipModalComponent,
    NewEditCustomerUserMembershipModalConfig,
    NewEditCustomerUserMembershipModalResult
} from '../../modals/new-edit-customer-user-membership-modal/new-edit-customer-user-membership-modal.component';
import {CpmCCustomerApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-common/cpmc-customer.api-model';
import {
    ETableColDataType,
    ETableColFilterType,
    TableTagDef
} from '../../../../sdk/src/components/generic-table/table-col-def';
import {CpmCUserRoleApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-common/cpmc-user-role.api-model';
import {CpmCCustomerUserMembershipApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-customer-user-membership.api-service';
import {CpmCCustomerApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-customer.api-service';
import {CpmCUserRoleApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-user-role.api-service';
import {EDeleteMode} from '../../../../sdk/src/enums/delete-mode.enum';
import {CpmCUserRoleApiUtils} from '../../../../gen/cpm-c-api-ts/src/utils/c-common/cpmc-user-role.api-utils';
import {ECpmCUserRoleTypeApiEnum} from '../../../../gen/cpm-c-api-ts/src/enums/cpmc-sdk.api-enums';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';

/**
 * CustomerUserMembership CRUD table component
 */
@Component({
    selector: 'app-customer-user-membership-crud',
    template: '<app-ibase-crud-content></app-ibase-crud-content>',
    imports: [PrimeNgImportsModule, SdkImportsModule]
})
export class CustomerUserMembershipCRUDComponent extends IBaseCRUDComponent<
    CpmCCustomerUserMembershipApiModel,
    NewEditCustomerUserMembershipModalComponent,
    NewEditCustomerUserMembershipModalConfig,
    NewEditCustomerUserMembershipModalResult
> {
    protected customers: CpmCCustomerApiModel[] = [];
    protected customerTags: TableTagDef[] = [];
    protected userRoles: CpmCUserRoleApiModel[] = [];
    protected userRoleTags: TableTagDef[] = [];

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.globalViewModelNamePrefix = '';
        this.configure({
            // Model config
            modelName: 'Customer User Membership',
            getModelHumanIdentifier: (model: CpmCCustomerUserMembershipApiModel) =>
                model.user
                    ? `${model.user.email} membership of ${this.customers.find((x) => x.uuid == model.customer_uuid)?.name ?? '<Unknown customer>'}`
                    : '<Unknown customer user membership>',

            // Service config
            deleteMode: EDeleteMode.CONFIRM,
            deleteModelService: (modelToDelete: CpmCCustomerUserMembershipApiModel) =>
                CpmCCustomerUserMembershipApiService.deleteCustomerUserMembership(modelToDelete.uuid!),

            // Modal config
            modalType: NewEditCustomerUserMembershipModalComponent,
            getDialogServiceParameters: NewEditCustomerUserMembershipModalComponent.getDialogServiceParameters,
            modalExtendedConfigBuilder: (model?: CpmCCustomerUserMembershipApiModel) =>
                new NewEditCustomerUserMembershipModalConfig(this.isFromGlobalView, model),

            // Authorization config
            authorizationNew: CpmCCustomerUserMembershipApiService.endpointNew(this.FAKE_UUID),
            authorizationEdit: CpmCCustomerUserMembershipApiService.endpointEdit(this.FAKE_UUID, this.FAKE_UUID),
            authorizationDelete: CpmCCustomerUserMembershipApiService.endpointDelete(this.FAKE_UUID, this.FAKE_UUID),
            commonAuthorizationNew: CpmCCustomerUserMembershipApiService.endpointNew(undefined),
            commonAuthorizationEdit: CpmCCustomerUserMembershipApiService.endpointEdit(undefined, this.FAKE_UUID),
            commonAuthorizationDelete: CpmCCustomerUserMembershipApiService.endpointDelete(undefined, this.FAKE_UUID)
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public async onReady(): Promise<void> {}
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On refresh (Error handling is done in the parent)
     */
    protected async onRefresh(): Promise<void> {
        await this.parallelSequencedAsyncCritical([
            {
                m: 'Loading customers...',
                f: async (): Promise<void> => {
                    if (!this.isFromGlobalView) return;

                    this.customers = (
                        await firstValueFrom(
                            CpmCCustomerApiService.getCustomerList({}, CpmCCustomerApiService.dPaginatorConfig)
                        )
                    ).data;

                    this.customerTags.splice(0, this.customerTags.length);
                    this.customerTags.push(
                        ...this.customers.map((x) => ({
                            style: {background: this.generateColorFromString(x.uuid + x.name, true)},
                            value: x.uuid!,
                            label: x.name
                        }))
                    );
                }
            },
            {
                m: 'Loading user roles...',
                f: async (): Promise<void> => {
                    this.userRoles = (
                        await firstValueFrom(
                            CpmCUserRoleApiService.getUserRoles(
                                {
                                    type: ECpmCUserRoleTypeApiEnum.MULTI_SCHEMA_TENANT_ROLE,
                                    include_all: this.isFromGlobalView
                                },
                                CpmCUserRoleApiService.dPaginatorConfig
                            )
                        )
                    ).data;

                    this.userRoleTags.splice(0, this.userRoleTags.length);
                    this.userRoleTags.push(
                        ...this.userRoles.map((x) => ({
                            style: {background: this.generateColorFromString(x.uuid + x.name, true)},
                            value: x.uuid!,
                            label: this.formatUserRoleName(x)
                        }))
                    );
                }
            },
            {
                m: 'Loading customer user memberships...',
                f: async (): Promise<void> => {
                    const result = await firstValueFrom(
                        CpmCCustomerUserMembershipApiService.getCustomerUserMembershipList({}, this.paginatorConfig)
                    );

                    this.items = result.data;
                    this.totalItemCount = result.total;
                }
            }
        ]);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected async initColumns(): Promise<void> {
        this.tableCols = [
            {
                field: 'customer_uuid',
                header: 'Customer',
                dataType: ETableColDataType.TAGS,
                searcheable: false,
                filterable: true,
                filterType: ETableColFilterType.SELECT,
                filterSelectOptions: this.customerTags,
                tags: this.customerTags
            },
            {
                field: 'user.email',
                header: 'User',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            },
            {
                field: 'user_role_uuid',
                header: 'User Role',
                dataType: ETableColDataType.TAGS,
                searcheable: false,
                filterable: true,
                filterType: ETableColFilterType.SELECT,
                filterSelectOptions: this.userRoleTags,
                tags: this.userRoleTags
            },
            {
                field: 'uuid',
                header: 'Is expired',
                dataType: ETableColDataType.BOOLEAN_ICON,
                searcheable: false,
                filterable: false,
                fnCustomData(row: CpmCCustomerUserMembershipApiModel) {
                    return row.expiration_date ? new Date(row.expiration_date) < new Date() : false;
                }
            }
        ];

        if (!this.isFromGlobalView) {
            this.tableCols.splice(
                this.tableCols.findIndex((x) => x.field == 'customer_uuid'),
                1
            );
        }

        this.addCreatedColumns();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Format a UserRole name for display to add informations about the scope
     * @param userRole Input UserRole
     * @returns Formatted UserRole name
     */
    private formatUserRoleName(userRole: CpmCUserRoleApiModel) {
        const customer = this.customers?.find((x) => x.uuid == userRole.multi_schema_tenant_uuid);
        return CpmCUserRoleApiUtils.formatUserRoleName(userRole, customer, !this.isFromGlobalView);
    }
}
